import { useState } from 'react';
import clsx from 'clsx';
import { useSearchParams } from 'react-router-dom';
import { useBrainStore } from '@/state/brainStore';
import { useSessionStore } from '@/state/sessionStore';
import { useSimulationStore } from '@/state/simulationStore';
import type { StudioId } from '@/domain/types';
import PayoutCashPanel from '@/screens/payouts/PayoutCashPanel';
import DayClosePanel from '@/screens/payouts/DayClosePanel';
import PayoutHistoryPanel from '@/screens/payouts/PayoutHistoryPanel';

/**
 * Payouts & cash (Prompt E2) — the money surface, split out from the floor
 * (which is for running clients). Per studio: each artist's envelope, on-screen
 * sign-off, the cash ledger (sealed float, working cash, deferral vs float-dip)
 * and the day's close.
 *
 * Two tabs, because the register is a different job from running the day: Today
 * is the operating screen, History is the record. It is a tab rather than another
 * panel so the day screen keeps its cards, ledger and close ritual uncrowded
 * (client, 30 Jul). Deep-linkable via ?tab=.
 */
const TABS = [
  { key: 'today', label: 'Today' },
  { key: 'history', label: 'History' },
] as const;

type TabKey = (typeof TABS)[number]['key'];
const TAB_KEYS = new Set<string>(TABS.map((t) => t.key));

export default function PayoutsCash() {
  const studios = useBrainStore((s) => s.studios);
  const clockIso = useSimulationStore((s) => s.clockIso);
  const assignedStudios = useSessionStore((s) => s.assignedStudios);
  const persona = useSessionStore((s) => s.persona);
  const [searchParams, setSearchParams] = useSearchParams();

  const rawTab = searchParams.get('tab') ?? 'today';
  const tab: TabKey = (TAB_KEYS.has(rawTab) ? rawTab : 'today') as TabKey;
  const setTab = (next: TabKey) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'today') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  const allowAll = persona === 'georgina' || persona === 'owner';
  const activeStudios = Object.values(studios).filter((s) => !s.archived);
  const [studioId, setStudioId] = useState<StudioId>(
    (allowAll ? undefined : assignedStudios[0]) ?? (activeStudios[0]?.id as StudioId),
  );
  const studio = studios[studioId] ?? activeStudios[0];

  return (
    <section className="px-8 py-8" data-testid="payouts-cash">
      <header className="flex items-end justify-between max-w-7xl flex-wrap gap-4">
        <div>
          <p className="label-small-caps text-gold-bright">Money</p>
          <h1 className="heading-display text-3xl text-cream mt-2">Payouts &amp; cash</h1>
          <p className="font-serif text-cream-soft mt-3 max-w-2xl">
            Pay each artist their cash envelope at their finish and keep the till honest. Each
            envelope becomes signable once that artist&rsquo;s last booking is checked out, and is
            paid whole or not at all &mdash; a day the takings cannot cover waits, intact, for the
            next payment that can. Break the sealed float only as a logged emergency.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="font-inter text-[10px] uppercase tracking-widest text-cream-soft">
            Studio
          </label>
          <select
            value={studio?.id ?? ''}
            onChange={(e) => setStudioId(e.target.value as StudioId)}
            className="bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-1.5 text-sm text-cream"
            data-testid="payouts-studio-filter"
          >
            {activeStudios.map((s) => (
              <option key={s.id} value={s.id}>
                {s.brand} · {s.name}
              </option>
            ))}
          </select>
        </div>
      </header>

      <nav
        className="mt-6 flex flex-wrap gap-1 border-b border-gold-dim/20 pb-2 max-w-7xl"
        role="tablist"
        data-testid="payouts-tabs"
      >
        {TABS.map((t) => {
          const active = t.key === tab;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(t.key)}
              data-testid={`payouts-tab-${t.key}`}
              className={clsx(
                'px-3 py-1.5 text-xs font-inter uppercase tracking-widest rounded-md transition border',
                active
                  ? 'bg-gold/15 text-gold-bright border-gold-dim/40'
                  : 'text-cream-soft border-transparent hover:text-gold-bright',
              )}
            >
              {t.label}
            </button>
          );
        })}
      </nav>

      <div className="mt-6 max-w-7xl">
        {!studio ? (
          <p className="font-inter text-[10px] uppercase tracking-widest text-cream-faint">
            No active studios.
          </p>
        ) : tab === 'history' ? (
          <PayoutHistoryPanel studioId={studio.id} studioName={studio.name} dayIso={clockIso} />
        ) : (
          <>
            <PayoutCashPanel studioId={studio.id} studioName={studio.name} dayIso={clockIso} />
            <DayClosePanel studioId={studio.id} studioName={studio.name} dayIso={clockIso} />
          </>
        )}
      </div>
    </section>
  );
}
